import { colorToCss } from "../../color/color"
import { createNumRangeProvider, NumericRangeColorProvider, NumericValueColorProvider } from "../../color/provider"
import { LayerTypeId } from "../../generated/enums"
import { groupBy, selectMany } from "../../utils/array"
import { isDefined, isFunction, isNumber, isObject, isUndefinedOrNull } from "../../utils/utils"
import { IDatalayerKey, IDatalayerKeyItem, IDatalayerMapInstance, IDataLayerRange, IDataLayerType, IDatalayerViewer, IDatalayerViewerFactory, IDatalayerZoneMapInstance, INumericValuesViewerOptions, ZoneTextLocation } from "../types"
import { getKeyFromRanges, getRangeColor, getRangeLabel } from "./range"

type ColorProvider = NumericValueColorProvider

export class NumericValuesViewer<Zone extends IDatalayerZoneMapInstance = IDatalayerZoneMapInstance> implements IDatalayerViewer {

    static factory(type: LayerTypeId, name: string, options?: INumericValuesViewerOptions): IDatalayerViewerFactory {

        return {
            typeId: type,
            name,
            createViewer: layer => new NumericValuesViewer(layer, options)
        }

    }

    protected readonly _type: IDataLayerType
    protected readonly _customType: boolean

    protected _dataProperty: string

    protected _units: string

    protected colorProvider: ColorProvider

    protected _minZoneValue: number
    protected _maxZoneValue: number
    protected _distinctValues: number[]

    protected _ranges: {
        min: number,
        max: number,
        color: number[],
        label: string
    }[]

    protected keyId: {
        id: string
        name: string
    }

    protected _visibleFeatureLabels: number[]

    constructor(protected readonly _layer: IDatalayerMapInstance<Zone>, protected options?: INumericValuesViewerOptions) {

        this.options = options = options || {}

        this._dataProperty = options.dataProperty
        this._units = options.units

        const type = this._type = options.type ?? _layer.customType ?? _layer.type
        this._customType = (type === _layer.customType) && Boolean(type)

        this.setupNumericLayer(type, options.colorProvider, options.ranges)

        this.refresh()

    }

    get type() {
        return this._type || this._layer.type
    }

    get typeId() {
        return this._layer.typeId
    }

    get units() {
        return isUndefinedOrNull(this._units)
            ? this._layer.valueUnits || this.type.Units
            : this._units
    }

    get minValue(): number {
        return this._minZoneValue
    }

    get maxValue(): number {
        return this._maxZoneValue
    }

    get distinctValues(): number[] {
        return this._distinctValues
    }

    public refresh() {
        this.setupMinMaxZoneValues()
        this.setupVisibleLabels()
    }

    protected setupMinMaxZoneValues() {
        const _ = this.setupMinMaxZoneValuesReturnValues()
        return
    }

    protected setupMinMaxZoneValuesReturnValues() {

        const { options } = this

        let layers = this.getLayersForValueRange() || []

        if (options.layerFilter) {
            layers = layers.filter(l => (l == this._layer) || options.layerFilter(l))
        }

        let values = selectMany(layers.map(l => {

            var ll = this.filterVisibleZones(l.zones)

            if (ll && ll.length) {
                return ll.map(z => this.getZoneValue(z))
            }
            else if (l.points && l.points.length) {
                return l.points.map(p => p[2])
            }

        })).filter(v => isNumber(v))

        if (this.options.valueFilter) {
            values = values.filter(v => this.options.valueFilter(v))
        }

        this._distinctValues = Array.from(new Set(values)).sort((v1, v2) => (v1 || 0) - (v2 || 0))

        this._minZoneValue = Math.min.apply(Math, this._distinctValues)
        this._maxZoneValue = Math.max.apply(Math, this._distinctValues)
        return values

    }

    protected setupVisibleLabels() {

        this._visibleFeatureLabels = null

        if ((!this.type.DeduplicateLabels) || this._layer.labelsNoDeduplicate) {
            return
        }

        let labels = this._layer.zones.map(z => {

            return {
                zone: z,
                label: this.getZoneText(z, ZoneTextLocation.MapFeature),
                area: z.area
            }

        })

        let groups = groupBy(labels, l => l.label)

        this._visibleFeatureLabels = groups.map(g => {

            let largest = (g.values.sort((z1, z2) => z2.area - z1.area))[0]

            return largest.zone.zone.DataLayerZoneId

        })

    }


    protected getLayersForValueRange() {

        let layers: IDatalayerMapInstance[] = [this._layer]

        const getLayers = this._layer.context.getAllLayers

        if (isFunction(getLayers)) {

            const typeId = this.typeId

            layers = layers.concat(
                (getLayers() || []).filter(l => l.typeId == typeId)
            )

        }

        return layers

    }

    getKey(): IDatalayerKey {

        const id = this.keyId

        if (this._ranges) {

            const key = this.getKeyFromRanges()

            return {
                ...id,
                typeKey: key,
                layerKey: key,
                numeric: true,
                fixed: true,
                unit: this._units,
            }

        }

        let zones = this.filterVisibleZones(this._layer.zones)

        let values = zones.map(z => {
            return {
                value: this.getZoneValue(z),
                fillColorValue: this.getZoneValueForFillColor(z)
            }
        })

        values = values.filter(v => !isNaN(v.value))

        let groups = groupBy(values, v => v.value)

        groups = groups.sort((g1, g2) => g1.key - g2.key)

        const items = groups.map(g => {

            const value = g.key

            let fillColorValue = g.values[0].fillColorValue
            fillColorValue = isUndefinedOrNull(fillColorValue) ? value : fillColorValue

            return this.getKeyEntry(value, colorToCss(this.getValueFillColor(fillColorValue)))

        })

        let key = {
            ...id,
            typeKey: items,
            layerKey: items,
            numeric: true,
            fixed: false
        }

        return key

    }

    protected getKeyEntry(value: any, color: string): IDatalayerKeyItem {

        return {
            value,
            text: this.getValueText(value, ZoneTextLocation.Key, null),
            color
        }

    }

    protected getKeyFromRanges() {

        return getKeyFromRanges(this._ranges)

    }

    protected filterVisibleZones(zones: IDatalayerZoneMapInstance[]): IDatalayerZoneMapInstance[] {

        return zones.filter(z => this.getZoneVisibility(z))

    }

    protected setupNumericLayer(type: IDataLayerType, colorProvider: ColorProvider, ranges?: IDataLayerRange[]) {

        this.keyId = this.options.keyId || {
            id: `type-${type.TypeId}`,
            name: type.Name,
        }

        ranges = ranges ?? this.getDefaultRanges(type)

        this.colorProvider = colorProvider ?? (
            ranges
                ? undefined
                : createColorProvider(type, this.options?.debug)
        )

        if (ranges) {

            this._ranges = ranges
                .map(r => ({ min: r.Min, max: r.Max, color: r.FillColor, label: r.Label }))
                .sort((a, b) => a.min - b.min)

        }

    }


    protected getDefaultRanges(type: IDataLayerType) {

        return type.Ranges

    }

    public getFeatureFillColor(feature: any) {

        let color = [255, 255, 255, 0.5]

        const zone = <{ value: any }>feature.get("data")

        if (zone) {

            var value = this.getZoneValueForFillColor(zone)

            if (isDefined(value)) {
                color = this.getValueFillColor(value, 1) || color
            }

        }

        return color

    }

    public getValueFillColor(value: number, alpha: number = 1) {

        if (isNumber(value)) {

            if (this._ranges) {
                return getRangeColor(this._ranges, value)
            }

            if (this.colorProvider) {

                var color = this.colorProvider.getColorForValue(value, this._minZoneValue, this._maxZoneValue)

                if (color) {
                    color = color.slice()
                    color[3] = alpha
                }

                return color

            }


        }

    }

    getZoneVisibility(zone: IDatalayerZoneMapInstance): boolean {
        return true
    }


    protected getZoneValueForFillColor(zone: { value: any }) {

        return this.getZoneValue(zone)

    }

    public getZoneValue(zone: { value: any }): number {

        if (zone) {

            if (this._dataProperty && isObject(zone.value) && isObject(zone.value.Data)) {
                return <number>(zone.value.Data[this._dataProperty])
            }

            return <number>zone.value

        }

    }

    getZoneText(zone: IDatalayerZoneMapInstance, location: ZoneTextLocation): string {

        if (this._visibleFeatureLabels && (location == ZoneTextLocation.MapFeature)) {

            if (this._visibleFeatureLabels.indexOf(zone.zone.DataLayerZoneId) < 0) {
                return ""
            }

        }

        const value = this.getZoneValue(zone)

        return this.getValueText(value, location, zone)

    }

    getValueText(value: number, location?: ZoneTextLocation, zone?: IDatalayerZoneMapInstance): string {

        const text = this._ranges
            ? getRangeLabel(this._ranges, value)
            : (!!value || value === 0) && !isNaN(value) ? value.toString() : ""

        return this.addUnits(text)

    }

    protected addUnits(text: string) {

        if (text) {
            const u = this.units
            if (u) {
                return this._customType
                    ? text.replace("%unit", u)
                    : text + " " + u
            }
        }

        return text
    }

    getPointFillColor(feature: any): any {
        return this.getFeatureFillColor(feature)
    }

    getClusterPointFillColor(values: any[]): any {

        if (values && values.length) {
            var value = values.reduce((v1, v2) => v1 + v2)
            value /= values.length
            return this.getValueFillColor(value)
        }

        return [255, 255, 255, 0.75]

    }

    get showStroke() {
        return this.options.showStroke
    }


}

function createColorProvider(type: IDataLayerType, debug = false) {

    if (type.Ranges) {
        return undefined
    }

    const style = type.Style

    if (style && style.Gradient) {
        return new NumericRangeColorProvider(style.Gradient, debug)
    }

    const color1 = [255, 239, 156]
    const color2 = [255, 113, 40]

    return createNumRangeProvider(color1, color2)

}
